import React, {
  useContext,
  useMemo,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react'
import {
  ActivityIndicator,
  Text as RNText,
  TouchableOpacity,
  LayoutChangeEvent,
  LayoutRectangle,
  Pressable,
} from 'react-native'

import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated'

import { useFormikContext } from 'formik'

import { Element } from '../Element'
import { Stack, StackProps } from '../Stack'
import { Icon } from '../Icon'
import { useIsMounted } from '../useIsMounted'
import { Button } from '../Button'

import { ComponentsContext, TokensContext, BreakpointContext } from '../context'
import { FormErrorContext } from './context'
import { applyStyles } from '../util/applyStyles'

interface FormSubmitButtonProps {
  align?: StackProps['alignItems']
  size?: number
  testID?: string
  variant?: string

  hoverVariant?: string
  successVariant?: string
  errorVariant?: string
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)

export const FormSubmitButton = ({
  align,
  children,
  size = 5,
  testID = 'FORM_SUBMIT_BUTTON',
  variant,
  hoverVariant = 'hover',
  successVariant = 'success',
  errorVariant = 'error',
}: FormSubmitButtonProps & { children: React.ReactNode }) => {
  const formikContext = useFormikContext()
  const [flashSuccess, setFlashSuccess] = useState(false)
  const [flashError, setFlashError] = useState(false)
  const prevSubmitting = useRef(formikContext.isSubmitting)
  const [isHovered, setIsHovered] = useState(false)

  const buttonDimensions = useRef<
    LayoutRectangle | { width: undefined; height: undefined }
  >({
    width: undefined,
    height: undefined,
  })

  const isMounted = useIsMounted()

  const formErrorContext = useContext(FormErrorContext)

  // To avoid the React warning of useLayoutEffect on the server
  if (!__SERVER__) {
    useLayoutEffect(() => {
      if (
        prevSubmitting.current &&
        !formikContext.isSubmitting &&
        isMounted()
      ) {
        if (Object.keys(formikContext.errors).length || formErrorContext) {
          setFlashError(true)
          setFlashSuccess(false)

          const timeoutId = setTimeout(() => {
            if (isMounted()) {
              setFlashError(false)
            }
          }, 2000)

          return () => {
            clearTimeout(timeoutId)
          }
        } else {
          setFlashSuccess(true)
          setFlashError(false)

          const timeoutId = setTimeout(() => {
            if (isMounted()) {
              setFlashSuccess(false)
            }
          }, 2000)

          return () => {
            clearTimeout(timeoutId)
          }
        }
      }

      if (formikContext.isSubmitting) {
        setFlashSuccess(false)
        setFlashError(false)
      }

      prevSubmitting.current = formikContext.isSubmitting
    }, [formikContext.isSubmitting, formErrorContext, isMounted])
  }

  const tokensContext = useContext(TokensContext)
  const componentsContext = useContext(ComponentsContext)
  const breakpointContext = useContext(BreakpointContext)

  const computedVariant = useMemo(() => {
    if (flashSuccess) {
      return successVariant
    }

    if (flashError) {
      return errorVariant
    }

    if (hoverVariant && isHovered) {
      return hoverVariant
    }

    if (formikContext.isValid) {
      return variant
    }

    return 'disabled'
  }, [
    formikContext.isValid,
    flashSuccess,
    flashError,
    successVariant,
    errorVariant,
    variant,
    hoverVariant,
    isHovered,
  ])

  const buttonStyle = useMemo(() => {
    return applyStyles(
      tokensContext,
      componentsContext,
      breakpointContext,
      {},
      'FormSubmitButton',
      computedVariant
    )
  }, [tokensContext, componentsContext, breakpointContext, computedVariant])

  const buttonColor0Animation = useSharedValue(
    buttonStyle.linearGradientColorsFrom as string
  )
  const buttonColor1Animation = useSharedValue(
    buttonStyle.linearGradientColorsTo as string
  )
  const borderColorAnimation = useSharedValue(buttonStyle.borderColor as string)

  useEffect(() => {
    buttonColor0Animation.value = withTiming(
      buttonStyle.linearGradientColorsFrom as string,
      {
        duration: 250,
      }
    )
    buttonColor1Animation.value = withTiming(
      buttonStyle.linearGradientColorsTo as string,
      {
        duration: 250,
      }
    )
    borderColorAnimation.value = withTiming(buttonStyle.borderColor as string, {
      duration: 250,
    })
  }, [buttonStyle])

  const animatedStyle = useAnimatedStyle(
    () => {
      return {
        ...buttonStyle,
        minWidth: buttonDimensions.current.width,

        background: `linear-gradient(${buttonColor0Animation.value}, ${buttonColor1Animation.value})`,
        borderColor: borderColorAnimation.value,
      } as Record<string, unknown>
    },
    [buttonStyle, buttonDimensions.current.width]
  )

  const buttonContent = useMemo(() => {
    if (flashSuccess) {
      return <Icon name='checkmark-filled' variant='button' />
    }

    if (flashError) {
      return <Icon name='dismiss-filled' variant='button' />
    }

    if (formikContext.isSubmitting) {
      return <ActivityIndicator color={tokensContext.color.grey[0]} />
    }

    return (
      <Element
        as={RNText}
        component='FormSubmitButtonText'
        testID={testID}
        tokens={{ fontSize: size, lineHeight: size }}
        variant={computedVariant}
      >
        {children}
      </Element>
    )
  }, [
    formikContext.isSubmitting,
    children,
    computedVariant,
    flashSuccess,
    flashError,
    tokensContext,
  ])

  const handleLayout = useCallback(
    ({ nativeEvent }: LayoutChangeEvent) => {
      if (!flashSuccess && !flashError && !formikContext.isSubmitting) {
        buttonDimensions.current = nativeEvent.layout
      }
    },
    [flashSuccess, flashError, formikContext.isSubmitting, children]
  )

  const handleHoverIn = useCallback(() => {
    if (hoverVariant) {
      setIsHovered(true)
    }
  }, [setIsHovered, hoverVariant])

  const handleHoverOut = useCallback(() => {
    if (hoverVariant) {
      setIsHovered(false)
    }
  }, [hoverVariant])

  return (
    <Stack alignItems={align}>
      <AnimatedPressable
        disabled={!formikContext.isValid}
        style={animatedStyle}
        onLayout={handleLayout}
        onPress={() => {
          if (!formikContext.isSubmitting) {
            formikContext.submitForm()
          }
        }}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
      >
        {buttonContent}
      </AnimatedPressable>
    </Stack>
  )
}
